import React, { useId } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { LntButton, LntDrawer, LntForm, LntFormItem, LntSelect } from '@app/components/base'

import { postTranslationsClusters } from '@app/api/routes/translationsConfig'
import { getClusters } from '@app/api/routes/locationСlusters'

import { LOCATION_KEY, TRANSLATIONS_CONFIG_KEY } from '@app/constants/queryKeys'
import { FILTERS_PLACEHOLDERS } from '@app/constants/filters'
import { SIZE } from '@app/constants'

import { getFilterFromEntities } from '@app/utils/entitiesAdapters'

import { useNotification } from '@app/hooks'

import styles from './styles.module.scss'

import type { TranslationsAddClustersDrawerProps } from './types'
import type { LntSelectProps } from '@app/components/base/Select/types'
import type { PostTranslationsClustersData } from '@app/api/routes/translationsConfig/types'

const commonSelectProps: LntSelectProps = {
  showArrow: true,
  size: SIZE.middle
}

export const TranslationsAddClustersDrawer: React.FC<TranslationsAddClustersDrawerProps> = props => {
  const { onClose } = props

  const form = useForm<PostTranslationsClustersData>()
  const isValidForm = form.formState.isValid

  const formId = useId()

  const queryClient = useQueryClient()
  const notification = useNotification()

  const translationsConfigClustersMutation = useMutation({
    mutationFn: postTranslationsClusters,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [TRANSLATIONS_CONFIG_KEY.clusters] })

      notification.success({
        message: 'Выбранные кластеры добавлены для транслирования.'
      })

      onClose?.()
    }
  })

  const handleSubmit = async (): Promise<void> => {
    const { ...formData } = form.getValues()

    translationsConfigClustersMutation.mutate(formData)
  }

  const footerButtons: React.ReactNode = (
    <div className={ styles.translationsAddClustersDrawer__footer }>
      <LntButton
        form={ formId }
        htmlType='submit'
        disabled={ !isValidForm }
      >
        Добавить
      </LntButton>
    </div>
  )

  return (
    <LntDrawer
      { ...props }
      title='Добавление кластера'
      width={ 424 }
      footer={ footerButtons }
    >
      <LntForm
        onSubmit={ handleSubmit }
        layout='vertical'
        formInstance={ form }
        id={ formId }
      >
        <LntFormItem
          name='fromClusterId'
          label='Выберите кластер'
          defaultValue={ [] }
          validationOptions={ { rules: { required: true } } }
          className={ styles.translationsAddClustersDrawer__formItem }
        >
          <LntSelect
            { ...commonSelectProps }
            placeholder={ FILTERS_PLACEHOLDERS.clustersFilter }
            querySelect={ {
              queryKey: [LOCATION_KEY.locationClustersItems],
              queryFn: getClusters,
              select: getFilterFromEntities
            } }
          />
        </LntFormItem>
        <LntFormItem
          name='toClusterId'
          label='Выберите кластер для транслирования'
          defaultValue={ [] }
          validationOptions={ { rules: { required: true } } }
          className={ styles.translationsAddClustersDrawer__formItem }
        >
          <LntSelect
            { ...commonSelectProps }
            placeholder={ FILTERS_PLACEHOLDERS.clustersFilter }
            querySelect={ {
              queryKey: [LOCATION_KEY.locationClustersItems],
              queryFn: getClusters,
              select: getFilterFromEntities
            } }
          />
        </LntFormItem>
      </LntForm>
    </LntDrawer>
  )
}
